import { useTranslation } from 'react-i18next'

import styles from './StepVisualProduct.module.scss'

/**
 * Шаг 01 «Подключаешь продукт» — миниатюра НАСТОЯЩЕГО экрана
 * `src/containers/AppWizard/steps/ProductStep.tsx` + `ProductReadout.tsx`, 320×180.
 *
 * Что взято дословно с экрана: поле ссылки с кнопкой «Read my product →»
 * (`app.steps.product.readIt`), серый блок разбора (что продаёт → WHO BUYS IT → кому,
 * `onboard.confirm.whoBuys`) и карточка сегмента с чипами должностей. Текст разбора и ссылка — из
 * реального конфига клиента `data/configs/quantumsoft.json`, должности — его `titles`.
 *
 * УБРАНО ПО ПРОСЬБЕ КЛИЕНТА и потому, что на 320px это шум, а не смысл: лента шагов мастера с
 * «step 1 of 6», полоса прогресса, лид с зелёной плашкой «free». Заодно снята голова карточки
 * «1 · Your product»: номер шага и его название стоят рядом с миниатюрой в самом блоке лендинга,
 * внутри картинки это был дубль. Осталось ровно действие шага — вставил ссылку, движок прочитал.
 *
 * ДВА ЭКРАНА В ПЕТЛЕ, а не один кадр. На настоящем экране поле ввода и разбор — это два состояния
 * ОДНОГО шага (пока `spec.sells` пуст — поле, после чтения — разбор). Раньше миниатюра рисовала их
 * вместе, и это было единственным местом, где она врала про продукт: такого кадра в мастере не
 * существует. Цикл 3 с + 5 с показывает их по очереди, как они и идут, — вставили ссылку, движок
 * прочитал. Всё время карточка держит одну и ту же высоту: блоки второго экрана занимают своё
 * место всегда, меняется только видимость, иначе поле ввода прыгало бы на каждом обороте.
 *
 * Что опущено ради 180 пикселей: логотип, кольцо с процентом и строка сообщения из AppHeader, три
 * последних чипа (обрезаются), подпись поля «Product link», карточка сайта с фавиконом из
 * ProductReadout и строка `who` под меткой сегмента.
 *
 * Ссылка и разбор под ней ИЛЛЮСТРАТИВНЫЕ: конфига planmasta нет ни в data/configs, ни в одном
 * прогоне на проде — я проверил, — поэтому настоящие sells/buyer/titles взять неоткуда. Оставить
 * под этой ссылкой разбор quantumsoft было бы прямой неправдой в карточке.
 *
 * Метка сегмента ВЫДУМАНА — как имена в соседних миниатюрах. Она приходит от модели
 * (`buyer_segments[].label`, «одно-четыре слова, называющие группу»), в конфиге клиента её нет, и
 * взять её неоткуда. Взять вместо неё продуктовую строку «Your buyer» нельзя: ProductReadout
 * печатает её только в запасной ветке, где `roles: []` — метка с чипами должностей была бы
 * состоянием, которого продукт не рисует.
 *
 * Служебные подписи — ключи t(). Данные продукта ими не становятся: должности копируются
 * дословно, отрасли и функции — таксономия LinkedIn, тексты разбора и черновика пишет модель,
 * имена и цитаты выдуманы. Всё это остаётся английским и в русской локали — как в продукте. Без зависимостей.* React говорит ей ровно одно слово — `playing`, то есть «строка доехала до кадра».
 */

/* Два сегмента, а не один: продукт их и печатает несколько (`buyer_segments[]`), а карточке они
   дают содержимое вместо воздуха внизу. Метки и должности ИЛЛЮСТРАТИВНЫЕ — конфига planmasta у
   нас нет ни в data/configs, ни в прогонах, поэтому взять настоящие неоткуда. */
const SEGMENTS = [
  { label: 'Early stage startups', roles: ['Founder', 'CTO'] },
  { label: 'Agencies & studios', roles: ['COO', 'Delivery Lead'] },
]

/* Ссылка разбита на символы не для красоты: набор печатается шагами `steps(21)`, и 21 — это длина
   именно этой строки. Держим их рядом, чтобы правка текста не разъехалась с числом шагов в SCSS. */
const LINK = 'https://planmasta.com'

/**
 * `playing` приходит от строки блока, когда та появилась в кадре. Без него цикл крутился бы за
 * экраном и читателя встречала бы середина: разбор уже раскрыт, а как он там оказался — не видно.
 * По умолчанию `true`, чтобы карточка сама по себе (песочница, тест) была живой.
 */
export const StepVisualProduct = ({ playing = true }: { playing?: boolean }) => {
  const { t } = useTranslation()

  return (
    <div className={playing ? `${styles.root} ${styles.playing}` : styles.root}>
      <div className={styles.body}>
        <div className={styles.linkRow}>
          <div className={styles.input}>
            {/* .field шириной ровно в строку: каретка ездит по нему от 0 до 100%, и это избавляет от
                подгонки в ch — у пропорционального шрифта ch не равен ширине буквы. */}
            <span className={styles.field}>
              <span className={styles.typed}>{LINK}</span>
              <i className={styles.caret} />
            </span>
          </div>
          <div className={styles.cta}>{t('app.steps.product.readIt')} →</div>
        </div>

        <div className={styles.readout}>
          <div className={styles.sells}>Project planning software for distributed teams</div>
          <div className={styles.key}>{t('onboard.confirm.whoBuys')}</div>
          <div className={styles.buyer}>Ops leads and founders running delivery without a PM</div>
        </div>

        {SEGMENTS.map((segment, index) => (
          <div key={segment.label} className={styles.segment}>
            <i className={styles.index}>{index + 1}</i>
            <b className={styles.label}>{segment.label}</b>
            <span className={styles.roles}>
              {segment.roles.map((role) => (
                <span key={role}>{role}</span>
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
